/**
 * CustomerDashboard - Customer's own profile view with autosave editing
 * Updated to match admin CustomerProfile truck styling exactly
 */

import React, { useState, useRef, useEffect } from 'react';
import { useOwnProfile } from '../../../hooks/useCustomers';
import { customerService } from '../../../services/customer.service';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Avatar,
} from '@mui/material';
import { TruckTimeline } from '../timeline/TruckTimeline';
import { TruckList } from '../CustomerProfile/components/TruckList';
import { DeleteTruckDialog } from '../CustomerProfile/components/DeleteTruckDialog';
import { useTruckManagement } from '../CustomerProfile/hooks/useTruckManagement';
import { useServiceHistory } from '../CustomerProfile/hooks/useServiceHistory';
import { useOCR } from '../CustomerProfile/hooks/useOCR';
import { getInitials } from '../../../utils/formatters';

interface CustomerData {
  name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
}

export const CustomerDashboard: React.FC = () => {
  const { data: customer, isLoading, error } = useOwnProfile();

  // Profile data state
  const [customerData, setCustomerData] = useState<CustomerData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });

  // Debounce timers for autosave
  const debounceTimers = useRef<{ [key: string]: ReturnType<typeof setTimeout> }>({});

  // Use truck management hooks from CustomerProfile (admin pattern)
  const {
    trucks,
    setTrucks,
    deleteModalOpen,
    truckToDelete,
    handleTruckUpdate,
    handleRegistrationDateChange,
    handleAddTruck,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTruckManagement(customer?.id, customer);

  const {
    expandedTruckId,
    serviceHistoryLoading,
    handleToggleExpand,
    handleAddServiceEntry,
    handleUpdateServiceStatus,
    handleUpdateServiceDate,
    handleDeleteServiceEntry,
  } = useServiceHistory(trucks, setTrucks);

  const {
    ocrLoading,
    vinInputRefs,
    licenseInputRefs,
    handleVinCamera,
    handleLicenseCamera,
    handleImageCapture,
  } = useOCR(handleTruckUpdate);

  // Initialize customer data when customer loads
  useEffect(() => {
    if (customer) {
      setCustomerData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
      });
    }
  }, [customer]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  /**
   * Handle field change with debounced autosave (500ms)
   */
  const handleFieldChange = (field: keyof CustomerData, value: string) => {
    if (!customer) return;

    // Update local state immediately
    setCustomerData(prev => ({ ...prev, [field]: value }));

    // Clear existing timer for this field
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    // Set new debounced save timer (500ms delay)
    debounceTimers.current[field] = setTimeout(async () => {
      try {
        await customerService.updateCustomer(customer.id, { [field]: value });
        // Silent success - autosave doesn't need feedback
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        // Revert on error
        if (customer) {
          setCustomerData({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
            address: customer.address || '',
            notes: customer.notes || '',
          });
        }
        alert(`Failed to update ${field}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }, 500);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Profile
          </Typography>
          <Typography color="textSecondary">
            {error instanceof Error ? error.message : 'Failed to load your profile'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" gutterBottom>
            Profile Not Found
          </Typography>
          <Typography color="textSecondary">
            Your customer profile could not be loaded. Please contact support.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Profile Information Card - Matches Admin Style */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          border: '1px solid #e5e7eb',
          borderRadius: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: '#111827',
              fontSize: '1.5rem',
              fontWeight: 600,
            }}
          >
            {getInitials(customerData.name || customer.name)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            {/* Customer Name */}
            <TextField
              fullWidth
              label="Customer Name"
              value={customerData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#111827',
                  backgroundColor: '#f9fafb',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  fontWeight: 600,
                },
                '& input': {
                  padding: '8px 12px',
                },
              }}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={customerData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="No email provided"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  fontWeight: 600,
                },
                '& input': {
                  padding: '8px 12px',
                },
              }}
            />

            {/* Phone */}
            <TextField
              fullWidth
              label="Phone"
              type="tel"
              value={customerData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  fontWeight: 600,
                },
                '& input': {
                  padding: '8px 12px',
                },
              }}
            />

            {/* Address */}
            <TextField
              fullWidth
              label="Address"
              value={customerData.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Street address"
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                  fontWeight: 600,
                },
                '& input': {
                  padding: '8px 12px',
                },
              }}
            />

            {/* Customer Since - Read Only */}
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#9ca3af',
              }}
            >
              Customer since {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
            </Typography>
          </Box>
        </Box>

        {/* Customer Notes */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={customerData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            placeholder="Add notes about your account..."
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                color: '#1f2937',
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& textarea': {
                padding: '8px 12px',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Truck Timeline Visualization - Matches Admin */}
      {trucks.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            border: '1px solid #e5e7eb',
            borderRadius: 1.5,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#111827',
              mb: 2,
            }}
          >
            Service Timeline
          </Typography>
          <TruckTimeline trucks={trucks} />
        </Paper>
      )}

      {/* Trucks Section - Uses Exact Same TruckList as Admin */}
      <TruckList
        trucks={trucks}
        expandedTruckId={expandedTruckId}
        ocrLoading={ocrLoading}
        serviceHistoryLoading={serviceHistoryLoading}
        vinInputRefs={vinInputRefs}
        licenseInputRefs={licenseInputRefs}
        onAddTruck={handleAddTruck}
        onToggleExpand={handleToggleExpand}
        onTruckUpdate={handleTruckUpdate}
        onRegistrationDateChange={handleRegistrationDateChange}
        onDeleteClick={handleDeleteClick}
        onVinCamera={handleVinCamera}
        onLicenseCamera={handleLicenseCamera}
        onImageCapture={handleImageCapture}
        onAddServiceEntry={handleAddServiceEntry}
        onUpdateServiceStatus={handleUpdateServiceStatus}
        onUpdateServiceDate={handleUpdateServiceDate}
        onDeleteServiceEntry={handleDeleteServiceEntry}
      />

      {/* Delete Confirmation Dialog - Matches Admin */}
      <DeleteTruckDialog
        open={deleteModalOpen}
        truck={truckToDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default CustomerDashboard;
